import { useEffect, useState } from 'react'
import {
  canShowAppDownloadModal,
  getAppDownloadModalState,
  markAppDownloadModalCompleted,
  markAppDownloadModalDismissed
} from './helpers'

interface UseAppDownloadModalVisibilityParams {
  campaignId?: string
  isCampaignValid: boolean
  isLoading?: boolean
  openDelaySeconds: number
  reopenAfterMinutes: number
  shouldEvaluate: boolean
}

interface UseAppDownloadModalVisibilityResult {
  handleDismiss: () => void
  handleStoreClick: () => void
  isBlockingLegacy: boolean
  shouldShow: boolean
}

export const useAppDownloadModalVisibility = ({
  campaignId,
  isCampaignValid,
  isLoading,
  openDelaySeconds,
  reopenAfterMinutes,
  shouldEvaluate
}: UseAppDownloadModalVisibilityParams): UseAppDownloadModalVisibilityResult => {
  const [isOpen, setIsOpen] = useState(true)
  const [isDelayCompleted, setIsDelayCompleted] = useState(false)
  const appDownloadModalState = getAppDownloadModalState(campaignId)
  const canOpen = Boolean(
    isCampaignValid &&
      canShowAppDownloadModal({
        campaignId,
        reopenAfterMinutes
      })
  )
  const shouldShow = Boolean(isOpen && canOpen && isDelayCompleted)
  const isBlockingLegacy = Boolean(
    shouldEvaluate && (isLoading || isCampaignValid)
  )

  const handleDismiss = () => {
    markAppDownloadModalDismissed(campaignId)
    setIsOpen(false)
  }

  const handleStoreClick = () => {
    markAppDownloadModalCompleted(campaignId)
    setIsOpen(false)
  }

  useEffect(() => {
    setIsOpen(true)
  }, [campaignId])

  useEffect(() => {
    if (!isCampaignValid || appDownloadModalState?.status !== 'dismissed') {
      return undefined
    }

    if (typeof appDownloadModalState.dismissedAt !== 'number') {
      setIsOpen(true)
      return undefined
    }

    const reopenAfterMilliseconds = reopenAfterMinutes * 60 * 1000
    const remainingMilliseconds =
      reopenAfterMilliseconds - (Date.now() - appDownloadModalState.dismissedAt)

    if (remainingMilliseconds <= 0) {
      setIsOpen(true)
      return undefined
    }

    const timeout = setTimeout(() => {
      setIsOpen(true)
    }, remainingMilliseconds)

    return () => {
      clearTimeout(timeout)
    }
  }, [
    appDownloadModalState?.dismissedAt,
    appDownloadModalState?.status,
    campaignId,
    isCampaignValid,
    reopenAfterMinutes
  ])

  useEffect(() => {
    if (!canOpen) {
      setIsDelayCompleted(false)
      return undefined
    }

    if (openDelaySeconds === 0) {
      setIsDelayCompleted(true)
      return undefined
    }

    setIsDelayCompleted(false)

    const timeout = setTimeout(() => {
      setIsDelayCompleted(true)
    }, openDelaySeconds * 1000)

    return () => {
      clearTimeout(timeout)
    }
  }, [campaignId, canOpen, openDelaySeconds])

  return {
    handleDismiss,
    handleStoreClick,
    isBlockingLegacy,
    shouldShow
  }
}
